<template>
  <Dialog
    :visible="modelValue"
    modal
    header="Cover Letter"
    class="w-full max-w-2xl"
    :draggable="false"
    @update:visible="emit('update:modelValue', $event)"
  >
    <div class="space-y-3 pt-2">
      <p class="text-sm text-gray-600">
        Compose a cover letter to accompany the transcript. It is included on the final PDF between the student
        information and the academic records.
      </p>
      <Textarea
        v-model="coverLetter"
        rows="10"
        autoResize
        class="w-full font-serif"
        placeholder="To Whom It May Concern,..."
        data-testid="cover-letter-input"
        :class="{ 'p-invalid': getErrorMessage('cover_letter') }"
      />
      <small v-if="getErrorMessage('cover_letter')" class="text-red-500">{{ getErrorMessage('cover_letter') }}</small>
      <div class="text-xs text-gray-400">{{ coverLetter.length }} characters · minimum 10</div>
    </div>

    <template #footer>
      <Button size="small" label="Cancel" severity="secondary" text data-testid="cover-letter-cancel" @click="close" />
      <Button size="small"
        label="Save"
        :loading="updatingCoverLetter"
        :disabled="updatingCoverLetter || coverLetter.trim().length < 10"
        data-testid="cover-letter-save"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useFormErrors } from '@/composables/useFormErrors';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';

const props = defineProps<{
  modelValue: boolean;
  generatedTranscriptId: string | null;
  initialCoverLetter?: string | null;
  requestId?: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  saved: [];
}>();

const coverLetter = ref('');
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const { saveCoverLetter, updatingCoverLetter } = useTranscriptMutations();

watch(() => props.modelValue, (open) => {
  if (open) {
    coverLetter.value = props.initialCoverLetter ?? '';
    clearErrors();
  }
});

const close = () => emit('update:modelValue', false);

const submit = async() => {
  if (!props.generatedTranscriptId) return;
  clearErrors();
  try {
    await saveCoverLetter(props.generatedTranscriptId, coverLetter.value, props.requestId ?? undefined);
    emit('saved');
    close();
  } catch (err) {
    handleServerErrors(err);
  }
};
</script>
